/**
 * Owns every RTSP stream the "Editar Fotos" modal starts.
 *
 * That modal is the one place allowed to run several ffmpeg streams at the same
 * time (see electron/rtspStreamPolicy.ts): its columns bypass the main process's
 * concurrency cap and same-camera dedup kill. Those two guards also happened to
 * be what reaped streams the UI lost track of, so with them bypassed the modal
 * has to be exact about what it started — hence one object that owns the whole
 * lifecycle instead of a bare Map spread across five callbacks.
 *
 * Four independent nets stop a leak:
 *   1. per-column: pointing a column at a camera always releases its previous one;
 *   2. one stream per FEED, not per column — columns watching the same video share
 *      a single refcounted ffmpeg process, so four columns never mean four sessions;
 *   3. teardown: stops each live stream AND asks the main process to kill this
 *      instance's ownerGroup, which catches ids the renderer somehow lost;
 *   4. closed-state: after teardown nothing can be started (a late restore
 *      timeout firing post-close would otherwise create an unowned stream) and
 *      nothing is kept alive, so the main process's 12s keepalive watchdog
 *      collects whatever is left.
 */

/**
 * Prefix of the tag carried by every stream a tracker starts.
 *
 * The full tag is per INSTANCE, not per component: four host modals embed
 * AllPhotosEditModal and OperationalView keeps the visitor one permanently
 * mounted with open=false. A shared tag would let that closed instance's
 * teardown group-stop the streams of whichever instance is actually open.
 */
export const ALL_PHOTOS_STREAM_GROUP_PREFIX = 'all-photos';

/** Disambiguates instances created within the same millisecond. */
let instanceSeq = 0;

/** Frames per second requested per column — matches the previous inline call. */
const STREAM_FPS = 20;
const STREAM_QUALITY = 'medium' as const;

export interface TrackedCamera {
  id: string;
  protocol: 'rtsp' | 'http' | 'mjpeg';
  ip_address: string;
  port: number;
  username?: string;
  password?: string;
  rtsp_path?: string;
}

/** The slice of window.electronAPI this tracker needs; absent in the browser. */
export interface StreamBridge {
  startIPCameraStream?: (config: Record<string, unknown>) => unknown;
  stopIPCameraStream?: (streamId: string) => unknown;
  stopIPCameraStreamGroup?: (ownerGroup: string) => unknown;
  keepaliveIPCameraStream?: (streamId: string) => unknown;
}

/** One ffmpeg stream, shared by every column pointed at that feed. */
interface SharedStream {
  streamId: string;
  columns: Set<string>;
}

const stripScheme = (ip: string) => ip.replace(/^(https?:\/\/|rtsp:\/\/)/i, '');

/**
 * What makes two columns "the same video", keyed on the connection rather than
 * the camera record: the same physical camera is routinely registered twice
 * (one record per photo slot), and two records of one feed must still share a
 * single ffmpeg session. Two channels of an NVR differ by path, so they stay
 * separate — sharing them would show channel 1 in both columns.
 */
function feedKey(camera: TrackedCamera): string {
  return [
    stripScheme(camera.ip_address),
    camera.port,
    camera.rtsp_path?.trim() ?? '',
    camera.username ?? '',
  ].join('|');
}

export class AllPhotosStreamTracker {
  /** colType → feed key (which shared stream the column is watching). */
  private byColumn = new Map<string, string>();
  /** feed key → the single stream serving it. */
  private byCamera = new Map<string, SharedStream>();
  private closed = false;
  private seq = 0;

  /** Tag every stream of THIS instance carries; group-stop targets exactly it. */
  readonly ownerGroup: string;

  constructor(
    private readonly getBridge: () => StreamBridge | undefined,
    private readonly now: () => number = () => Date.now(),
  ) {
    this.ownerGroup = `${ALL_PHOTOS_STREAM_GROUP_PREFIX}-${this.now()}-${instanceSeq++}`;
  }

  /** Re-arm after a teardown (called when the modal opens). */
  open(): void {
    this.closed = false;
  }

  /**
   * Point a column at a camera, releasing whatever it watched before.
   * Columns sharing a camera share ONE ffmpeg stream — opening four sessions
   * against one camera is what the bypassed dedup guard used to prevent, and
   * some cameras cap concurrent RTSP sessions outright.
   * Returns the stream id, or null when there is nothing to start.
   */
  start(colType: string, camera: TrackedCamera): string | null {
    if (this.closed) return null;
    if (camera.protocol !== 'rtsp') return null;
    const bridge = this.getBridge();
    if (!bridge?.startIPCameraStream) return null;

    // Release the column's previous camera first (stops that stream only if
    // this was its last watcher).
    this.stop(colType);

    const key = feedKey(camera);
    const existing = this.byCamera.get(key);
    if (existing) {
      existing.columns.add(colType);
      this.byColumn.set(colType, key);
      return existing.streamId;
    }

    const streamId = `${this.ownerGroup}-${camera.id}-${this.seq++}`;
    // Track BEFORE the call: if startIPCameraStream resolves late or the window
    // closes mid-flight, teardown must still know this id exists.
    this.byColumn.set(colType, key);
    this.byCamera.set(key, { streamId, columns: new Set([colType]) });

    try {
      bridge.startIPCameraStream({
        streamId,
        ownerGroup: this.ownerGroup,
        allowConcurrent: true,
        ipAddress: stripScheme(camera.ip_address),
        port: camera.port,
        username: camera.username,
        password: camera.password,
        rtspPath: camera.rtsp_path?.trim() ?? '',
        fps: STREAM_FPS,
        quality: STREAM_QUALITY,
      });
    } catch {
      // The stream never started — drop it so teardown does not stop a ghost.
      this.byColumn.delete(colType);
      this.byCamera.delete(key);
      return null;
    }

    return streamId;
  }

  /** Release a column; stops the stream only when no column is left on it. */
  stop(colType: string): void {
    const key = this.byColumn.get(colType);
    if (!key) return;
    this.byColumn.delete(colType);

    const shared = this.byCamera.get(key);
    if (!shared) return;
    shared.columns.delete(colType);
    if (shared.columns.size > 0) return;

    this.byCamera.delete(key);
    this.callBridge(b => b.stopIPCameraStream?.(shared.streamId));
  }

  /**
   * Stop everything this modal owns and refuse further starts until open().
   * A no-op once already closed: the modal's close-cleanup effect re-runs on
   * every parent render (one host rebuilds the `columns` array each time), and
   * re-firing IPC on each of those would be pure noise.
   */
  teardown(): void {
    if (this.closed) return;
    const ids = this.activeIds();
    this.byColumn.clear();
    this.byCamera.clear();
    this.closed = true;
    ids.forEach(streamId => this.callBridge(b => b.stopIPCameraStream?.(streamId)));
    // Backstop: kills this instance's streams the renderer no longer has ids for.
    this.callBridge(b => b.stopIPCameraStreamGroup?.(this.ownerGroup));
  }

  /** Ping every live stream once — the main process reaps after 12s of silence. */
  keepalive(): void {
    if (this.closed) return;
    this.activeIds().forEach(streamId => this.callBridge(b => b.keepaliveIPCameraStream?.(streamId)));
  }

  /**
   * Drop a stream the main process says has ended (ffmpeg died, or it was
   * evicted). Returns the columns that were watching it, so the caller can
   * clear previews that would otherwise sit frozen under an "AO VIVO" badge.
   * Does NOT ask the main process to stop anything — it is already gone.
   */
  forget(streamId: string): string[] {
    const columns = this.columnsFor(streamId);
    if (columns.length === 0) return [];
    columns.forEach(colType => this.byColumn.delete(colType));
    this.byCamera.forEach((shared, key) => {
      if (shared.streamId === streamId) this.byCamera.delete(key);
    });
    return columns;
  }

  /** Columns a frame belongs to; empty for superseded/foreign streams. */
  columnsFor(streamId: string): string[] {
    let columns: string[] = [];
    this.byCamera.forEach(shared => {
      if (shared.streamId === streamId) columns = Array.from(shared.columns);
    });
    return columns;
  }

  /** Stream ids currently owned (tests and diagnostics). */
  activeIds(): string[] {
    return Array.from(this.byCamera.values()).map(s => s.streamId);
  }

  private callBridge(fn: (bridge: StreamBridge) => unknown): void {
    const bridge = this.getBridge();
    if (!bridge) return;
    try {
      const result = fn(bridge);
      // IPC calls return promises; a rejection here must never break teardown.
      if (result && typeof (result as Promise<unknown>).catch === 'function') {
        (result as Promise<unknown>).catch(() => {});
      }
    } catch {
      // Bridge gone (window closing) — nothing left to do.
    }
  }
}
